import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../axiosSetup";
import DataTable from "./DataTable";
import cfg from "../../config";

const ZRWorkAssignmentList = () => {
  const [allData, setAllData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterFull, setFilterFull] = useState("offen");
  const navigate = useNavigate();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWorkAssignment, setSelectedWorkAssignment] = useState(null);
  const [personsList, setPersonsList] = useState([]);
  const [personSearchTerm, setPersonSearchTerm] = useState("");
  const [filteredPersonsList, setFilteredPersonsList] = useState([]);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [attendanceBulkModalOpen, setAttendanceBulkModalOpen] = useState(false);
  const [bulkWorkAssignment, setBulkWorkAssignment] = useState(null);
  const [attendanceStates, setAttendanceStates] = useState([]);

  useEffect(() => {
    const fetchWorkAssignments = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get("/api/all-work-assignment-zunftrat", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data || [];

        if (data.length > 0) {
          const first = data[0];
          const hdrs = [];
          for (const key in first) {
            if (key === "isFull" || key === "attendance") continue;
            if (first[key]?.frontend === false) continue;
            if (first[key] && typeof first[key] === "object" && "frontendName" in first[key]) {
              hdrs.push({ key, label: first[key].frontendName });
            } else {
              // Felder ohne frontendName tragen sonst ihren technischen Namen
              // als Spaltenkopf. Fuer begriffsabhaengige Felder reicht das
              // nicht — sie muessen dem Wert aus tblconfig folgen.
              const BESCHRIFTUNG = { zugeordneteZunftrat: `Zugeordnet: ${cfg.adminRoleName}` };
              hdrs.push({ key, label: BESCHRIFTUNG[key] ?? key });
            }
          }
          setHeaders(hdrs);
        }
        setAllData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkAssignments();
  }, []);

  useEffect(() => {
    if (!personSearchTerm.trim()) {
      setFilteredPersonsList(personsList);
    } else {
      const t = personSearchTerm.toLowerCase();
      setFilteredPersonsList(
        personsList.filter((p) => {
          const name = `${p.Vorname} ${p.Nachname}`.toLowerCase();
          const haes = p.haeserNumbers?.join(" ").toLowerCase() || "";
          return name.includes(t) || haes.includes(t);
        })
      );
    }
  }, [personsList, personSearchTerm]);

  const tableData = useMemo(() => {
    return allData.filter((item) => {
      let val = typeof item.isFull === "object" ? item.isFull?.value : item.isFull;
      if (typeof val === "string") val = parseInt(val, 10);
      if (filterFull === "offen" && val === 1) return false;
      if (filterFull === "voll" && val !== 1) return false;
      return true;
    });
  }, [allData, filterFull]);

  const columns = useMemo(
    () =>
      headers.map((h) => ({
        key: h.key,
        label: h.label,
        render: (val) => {
          if (h.key === "zugeordneteZunftrat") {
            if (!Array.isArray(val) || !val.length)
              return <span className="dt-muted">—</span>;
            return (
              <div>
                {val.map((p) => (
                  <div key={p.memberID} style={{ fontSize: 13 }}>
                    {p.memberName}
                  </div>
                ))}
              </div>
            );
          }
          if (!val || typeof val !== "object") return val != null ? String(val) : <span className="dt-muted">—</span>;
          const v = val.value;
          if (v === true || v === 1) return <span className="dt-badge dt-badge-yes">Ja</span>;
          if (v === false || v === 0) return <span className="dt-badge dt-badge-no">Nein</span>;
          return v != null ? String(v) : <span className="dt-muted">—</span>;
        },
        searchValue: (val) => {
          if (h.key === "zugeordneteZunftrat")
            return Array.isArray(val) ? val.map((p) => p.memberName).join(" ") : "";
          if (!val || typeof val !== "object") return String(val ?? "");
          return String(val.value ?? "");
        },
      })),
    [headers]
  );

  const openModal = async (workAssignment) => {
    setSelectedWorkAssignment(workAssignment);
    setModalOpen(true);
    setSelectedPersons(workAssignment?.attendance?.map((a) => String(a.memberID)) || []);
    try {
      const token = localStorage.getItem("authToken");
      const res = await api.get("/api/member-workAssignment-zunftrat", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPersonsList(res.data);
    } catch {
      setPersonsList([]);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedWorkAssignment(null);
    setSelectedPersons([]);
  };

  const handleOpenAttendanceBulk = (workAssignment) => {
    setBulkWorkAssignment(workAssignment);
    setAttendanceBulkModalOpen(true);
    setAttendanceStates(
      workAssignment.attendance.map((att) => ({
        id: att.id,
        memberID: att.memberID,
        status: att.present ? "anwesend" : att.excused ? "entschuldigt" : att.unexcused ? "unentschuldigt" : null,
        present: !!att.present,
        excused: !!att.excused,
        unexcused: !!att.unexcused,
        comment: att.comment || "",
      }))
    );
  };

  const handleCloseAttendanceBulk = () => {
    setBulkWorkAssignment(null);
    setAttendanceBulkModalOpen(false);
  };

  const actions = (row) => (
    <div className="dt-actions">
      <button
        className="dt-btn dt-btn-primary"
        onClick={() => navigate("/zr-workassignment-create", { state: { einsatzItem: row } })}
      >
        Bearbeiten
      </button>
      <button className="dt-btn dt-btn-secondary" onClick={() => openModal(row)}>
        Personen eintragen
      </button>
      {row.attendance?.length > 0 && (
        <button className="dt-btn dt-btn-warning" onClick={() => handleOpenAttendanceBulk(row)}>
          Anwesenheit eingeben
        </button>
      )}
    </div>
  );

  const expandable = (row) => {
    if (!row.attendance?.length) return null;
    return (
      <div className="dt-expand-attendance">
        <h6>Anwesenheiten ({row.attendance.length} Personen)</h6>
        <table className="dt-expand-table">
          <thead>
            <tr>
              <th>{cfg.memberTerm}</th>
              <th>Anwesend</th>
              <th>Entschuldigt</th>
              <th>Unentschuldigt</th>
              <th>Kommentar</th>
              <th>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {row.attendance.map((att) => (
              <tr key={att.id}>
                <td>{att.memberName}</td>
                <td>{att.present ? <span className="dt-badge dt-badge-yes">✓</span> : <span className="dt-badge dt-badge-no">✗</span>}</td>
                <td>{att.excused ? <span className="dt-badge dt-badge-warn">✓</span> : "—"}</td>
                <td>{att.unexcused ? <span className="dt-badge dt-badge-no">✓</span> : "—"}</td>
                <td style={{ maxWidth: 180, wordBreak: "break-word" }}>{att.comment || <span className="dt-muted">—</span>}</td>
                <td>
                  <button
                    className="dt-btn dt-btn-info"
                    onClick={() => { setSelectedAttendance(att); setAttendanceModalOpen(true); }}
                  >
                    Bearbeiten
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const headerActions = (
    <div className="d-flex gap-2 align-items-center flex-wrap">
      <select className="dt-select" value={filterFull} onChange={(e) => setFilterFull(e.target.value)}>
        <option value="offen">Nur offene Einsätze</option>
        <option value="voll">Nur vollständige Einsätze</option>
        <option value="alle">Alle Einsätze</option>
      </select>
      <button className="dt-btn dt-btn-success" onClick={() => navigate("/zr-workassignment-create")}>
        + Arbeitseinsatz erstellen
      </button>
    </div>
  );

  return (
    <div className="container-fluid px-3 px-md-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Arbeitseinsätze</h2>
      </div>

      <DataTable
        data={tableData}
        columns={columns}
        loading={loading}
        error={error}
        actions={actions}
        expandable={expandable}
        headerActions={headerActions}
        searchPlaceholder="Arbeitseinsatz suchen…"
        emptyText="Keine Arbeitseinsätze gefunden."
        initialPageSize={10}
        pageSizeOptions={[5, 10, 25]}
      />
      <br />

      {/* Modal: Personen eintragen */}
      {modalOpen && selectedWorkAssignment && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box animate-in" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>&times;</button>
            <h2 className="modal-title">Arbeitseinsatz-Details</h2>
            <div className="modal-section">
              <p><strong>Name:</strong> {selectedWorkAssignment.Name?.value || "–"}</p>
              <p><strong>Datum:</strong> {selectedWorkAssignment.dates?.value || "–"}</p>
              <p><strong>Bereits eingetragen:</strong> {selectedWorkAssignment.bookedCount?.value ?? "–"}</p>
              <p><strong>Gesamtanzahl:</strong> {selectedWorkAssignment.numberOfPeople?.value || "–"}</p>
            </div>
            <hr className="modal-divider" />
            <h3 className="modal-subtitle">Personen hinzufügen</h3>
            <input
              type="text"
              className="modal-input"
              placeholder="Person suchen..."
              value={personSearchTerm}
              onChange={(e) => setPersonSearchTerm(e.target.value)}
            />
            <div className="modal-person-list">
              {filteredPersonsList.length > 0 ? (
                filteredPersonsList.map((person) => {
                  const haesStr = person.haeserNumbers?.length > 0 ? person.haeserNumbers.join(" | ") + " | " : "";
                  return (
                    <label key={person.ID} className="person-item">
                      <input
                        type="checkbox"
                        checked={selectedPersons.includes(String(person.ID))}
                        onChange={(e) => {
                          const id = String(person.ID);
                          setSelectedPersons((prev) =>
                            e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                          );
                        }}
                      />
                      <span>{haesStr}{person.Vorname} {person.Nachname}</span>
                    </label>
                  );
                })
              ) : (
                <p style={{ color: "#777", textAlign: "center" }}>Keine Personen gefunden</p>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeModal}>Abbrechen</button>
              <button
                className="btn-save"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem("authToken");
                    await api.post(
                      "/api/create-member-to-work-assignment-zunftrat",
                      { workAssignmentID: selectedWorkAssignment.ID, member: selectedPersons },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    window.location.reload();
                    closeModal();
                  } catch (err) {
                    console.error("Fehler beim Speichern:", err);
                  }
                }}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Anwesenheit einzeln */}
      {attendanceModalOpen && selectedAttendance && (
        <div className="modal-overlay" onClick={() => setAttendanceModalOpen(false)}>
          <div className="modal-box animate-in" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setAttendanceModalOpen(false)}>&times;</button>
            <h2 className="modal-title">Anwesenheit bearbeiten</h2>
            <div className="modal-section">
              <p><strong>{cfg.memberTerm}:</strong> {selectedAttendance.memberName}</p>
            </div>
            <div className="modal-section d-flex flex-column gap-2">
              {["anwesend", "entschuldigt", "unentschuldigt"].map((status) => (
                <label key={status} className="form-check-label d-flex align-items-center gap-2">
                  <input
                    type="radio"
                    name="status"
                    checked={selectedAttendance.status === status}
                    onChange={() => setSelectedAttendance({
                      ...selectedAttendance,
                      status,
                      present: status === "anwesend",
                      excused: status === "entschuldigt",
                      unexcused: status === "unentschuldigt",
                    })}
                  />
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </label>
              ))}
            </div>
            <div className="modal-section">
              <label className="modal-subtitle">Kommentar</label>
              <textarea
                className="modal-input"
                rows={3}
                value={selectedAttendance.comment || ""}
                onChange={(e) => setSelectedAttendance({ ...selectedAttendance, comment: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setAttendanceModalOpen(false)}>Abbrechen</button>
              <button
                className="btn-save"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem("authToken");
                    await api.post(
                      "/api/update-attendance-member-to-work-assignment-zunftrat",
                      selectedAttendance,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    window.location.reload();
                    setAttendanceModalOpen(false);
                  } catch (err) {
                    console.error("Fehler beim Speichern:", err);
                  }
                }}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Anwesenheit bulk */}
      {attendanceBulkModalOpen && bulkWorkAssignment && (
        <div className="modal-overlay" onClick={handleCloseAttendanceBulk}>
          <div className="modal-box animate-in" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseAttendanceBulk}>&times;</button>
            <h2 className="modal-title">Anwesenheit eingeben</h2>
            <p><strong>Arbeitseinsatz:</strong> {bulkWorkAssignment.Name?.value || "Unbekannt"}</p>
            <div className="modal-person-list" style={{ maxHeight: 400, overflowY: "auto" }}>
              {bulkWorkAssignment.attendance.map((person) => {
                const current = attendanceStates.find((e) => e.memberID === person.memberID) || {};
                const updateState = (newData) =>
                  setAttendanceStates((prev) =>
                    prev.map((e) => (e.memberID === person.memberID ? { ...e, ...newData } : e))
                  );
                return (
                  <div key={person.memberID} className="person-card d-flex flex-column mb-3 p-3 bg-light rounded shadow-sm">
                    <p className="mb-2"><strong>{person.memberName}</strong></p>
                    <div className="d-flex flex-wrap gap-3 mb-2">
                      {[
                        { label: "Anwesend", status: "anwesend", present: true, excused: false, unexcused: false },
                        { label: "Entschuldigt", status: "entschuldigt", present: false, excused: true, unexcused: false },
                        { label: "Unentschuldigt", status: "unentschuldigt", present: false, excused: false, unexcused: true },
                      ].map(({ label, status, ...rest }) => (
                        <div key={status} className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name={`status-${person.memberID}`}
                            checked={current.status === status}
                            onChange={() => updateState({ status, ...rest })}
                          />
                          <label className="form-check-label">{label}</label>
                        </div>
                      ))}
                    </div>
                    <textarea
                      className="modal-input comment-box"
                      rows={2}
                      placeholder="Kommentar..."
                      value={current.comment || ""}
                      onChange={(e) => updateState({ comment: e.target.value })}
                    />
                  </div>
                );
              })}
            </div>
            <div className="modal-actions mt-3">
              <button className="btn-cancel" onClick={handleCloseAttendanceBulk}>Schließen</button>
              <button
                className="btn-save"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem("authToken");
                    await api.post(
                      "/api/update-multiple-attendance-member-to-work-assignment-zunftrat",
                      attendanceStates,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    window.location.reload();
                    handleCloseAttendanceBulk();
                  } catch (err) {
                    console.error("Fehler beim Speichern:", err);
                  }
                }}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZRWorkAssignmentList;
